// Local mirror of the last successful getMyTrip() response.
//
// Purpose: cold-launching offline must NOT block tracking. The /tracking screen
// and /offline-control route both read the cached trip id from here when the
// network query is unavailable. Writes happen automatically from inside
// getMyTrip() on success — callers do not need to remember to refresh.

import { kv } from "@/lib/native/kv";
import { useEffect, useState } from "react";

const KEY = "roava.cached_trip";
const EVENT = "roava:cached-trip-changed";

export interface CachedTrip {
  id: string;
  name: string | null;
  startDate: string | null;
  endDate: string | null;
  isAdmin: boolean;
  cachedAt: string;
}

let mem: CachedTrip | null = null;
let hydrated = false;
let hydrating: Promise<CachedTrip | null> | null = null;

function emit(t: CachedTrip | null) {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: t }));
  }
}

export async function hydrateCachedTrip(): Promise<CachedTrip | null> {
  if (hydrated) return mem;
  if (hydrating) return hydrating;
  hydrating = (async () => {
    try {
      const raw = await kv.get(KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as CachedTrip;
        if (parsed && typeof parsed.id === "string") mem = parsed;
      }
    } catch {
      /* ignore malformed cache */
    }
    hydrated = true;
    return mem;
  })();
  try {
    return await hydrating;
  } finally {
    hydrating = null;
  }
}

export function getCachedTripSync(): CachedTrip | null {
  return mem;
}

export async function getCachedTrip(): Promise<CachedTrip | null> {
  if (!hydrated) await hydrateCachedTrip();
  return mem;
}

export async function setCachedTrip(t: Omit<CachedTrip, "cachedAt"> | null): Promise<void> {
  if (t === null) {
    mem = null;
    await kv.remove(KEY);
    emit(null);
    hydrated = true;
    return;
  }
  const full: CachedTrip = { ...t, cachedAt: new Date().toISOString() };
  mem = full;
  hydrated = true;
  try {
    await kv.set(KEY, JSON.stringify(full));
  } catch {
    /* ignore quota errors */
  }
  emit(full);
}

/** Reactive hook. Hydrates on mount, re-renders on changes. */
export function useCachedTrip(): CachedTrip | null {
  const [t, setT] = useState<CachedTrip | null>(mem);
  useEffect(() => {
    let alive = true;
    void hydrateCachedTrip().then((v) => { if (alive) setT(v); });
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<CachedTrip | null>).detail;
      setT(detail);
    };
    if (typeof window !== "undefined") {
      window.addEventListener(EVENT, onChange);
    }
    return () => {
      alive = false;
      if (typeof window !== "undefined") {
        window.removeEventListener(EVENT, onChange);
      }
    };
  }, []);
  return t;
}
